import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchBySlug } from '../lib/cms';
import { Award, CheckCircle, ShieldCheck, Heart, Clock, Users } from 'lucide-react';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('om-os');
      if (data) {
        setPageData(data);
      }
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  const title = pageData?.title?.da || 'Om os';
  const metaTitle = pageData?.meta_title?.da || 'Om os - KBS Tømrer – Montage ApS';
  const metaDesc = pageData?.meta_description?.da || 'I 2013 stiftede Karsten Sørensen tømrerfirmaet KBS Tømrer – Montage ApS, der har adresse i Malling. Med mere end 25 år i branchen...';
  const heroImage = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/350.jpg';

  // Optimer hero-billede via Cloudflare resizing (~1600px bredde)
  const optimizedHero = heroImage.startsWith('https://assets.auxodev.dk')
    ? heroImage.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/')
    : heroImage;

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-950 text-white py-20 lg:py-28">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHero}
            alt={title}
            className="h-full w-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            {title}
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
            Historien om KBS Tømrer – Montage ApS og vores urokkelige fokus på kvalitet og ærlighed.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Text Content */}
            <div className="lg:col-span-7">
              <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-bold prose-p:leading-relaxed prose-p:text-slate-600 prose-strong:text-brand-charcoal">
                {pageData?.content?.da ? (
                  <div dangerouslySetInnerHTML={{ __html: pageData.content.da }} />
                ) : (
                  <p>Indholdet kunne ikke indlæses.</p>
                )}
              </div>

              {/* Core Values */}
              <div className="mt-12 border-t border-slate-100 pt-10">
                <h3 className="font-display text-xl font-bold text-brand-charcoal mb-6">
                  Vores 3 ufravigelige principper:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-5">
                    <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary w-fit mb-4">
                      <ShieldCheck className="h-6 w-6" />
                    </div>
                    <h4 className="font-bold text-brand-charcoal text-base mb-2">At være ærlige</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Vi rådgiver dig altid realistisk om opgavens omfang og pris, så der ikke kommer ubehagelige overraskelser.
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-5">
                    <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary w-fit mb-4">
                      <Clock className="h-6 w-6" />
                    </div>
                    <h4 className="font-bold text-brand-charcoal text-base mb-2">Overholde aftaler</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      En aftale er en aftale. Vi møder op til tiden og leverer arbejdet inden for den aftalte tidsramme.
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-5">
                    <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary w-fit mb-4">
                      <Award className="h-6 w-6" />
                    </div>
                    <h4 className="font-bold text-brand-charcoal text-base mb-2">Godt håndværk</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Vi går ikke på kompromis med kvaliteten. Vi udfører et ordentligt stykke arbejde, du kan glæde dig over i mange år.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Sidebar Info */}
            <div className="lg:col-span-5">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 shadow-md">
                <h3 className="font-display text-lg font-bold text-brand-charcoal mb-6 pb-4 border-b border-slate-200">
                  Virksomhedsfakta
                </h3>
                
                <ul className="space-y-4 text-sm">
                  <li className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Virksomhed:</span>
                    <span className="font-bold text-brand-charcoal text-right">KBS Tømrer – Montage ApS</span>
                  </li>
                  <li className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Stifter & Ejer:</span>
                    <span className="font-bold text-brand-charcoal text-right">Karsten Sørensen</span>
                  </li>
                  <li className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Etableret:</span>
                    <span className="font-bold text-brand-charcoal text-right">2013</span>
                  </li>
                  <li className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Erfaring i branchen:</span>
                    <span className="font-bold text-brand-charcoal text-right">Mere end 25 år</span>
                  </li>
                  <li className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">CVR-nummer:</span>
                    <span className="font-bold text-brand-charcoal text-right">35246714</span>
                  </li>
                  <li className="flex justify-between py-2">
                    <span className="text-slate-500 font-medium">Adresse:</span>
                    <span className="font-bold text-brand-charcoal text-right">Balskærvej 26, 8340 Malling</span>
                  </li>
                </ul>

                {/* Trust Badge */}
                <div className="mt-8 rounded-xl bg-brand-primary/5 border border-brand-primary/10 p-5 flex items-start gap-4">
                  <div className="rounded-lg bg-brand-primary text-white p-2 shrink-0">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-charcoal text-sm">Garanti for tryghed</h4>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Som medlem af 3byg og med over 25 års erfaring er du sikret et professionelt forløb fra første snak til færdigt resultat.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
